import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

type Client = { id: string; name: string | null; business_name: string | null; website_url: string | null; public_slug: string | null };
type Post = {
  id: string;
  title: string | null;
  slug: string | null;
  meta_description: string | null;
  featured_image: string | null;
  published_at: string | null;
};

export default function SiteBlog() {
  const { clientSlug } = useParams();
  const [client, setClient] = useState<Client | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      if (!clientSlug) return;
      const { data: c } = await supabase
        .from("clients")
        .select("id,name,business_name,website_url,public_slug")
        .eq("public_slug", clientSlug)
        .maybeSingle();
      if (!c) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setClient(c as Client);
      const { data: p } = await supabase
        .from("blog_posts")
        .select("id,title,slug,meta_description,featured_image,published_at")
        .eq("client_id", c.id)
        .eq("published", true)
        .order("published_at", { ascending: false });
      setPosts((p ?? []) as Post[]);
      setLoading(false);
    })();
  }, [clientSlug]);

  useEffect(() => {
    if (!client) return;
    const name = client.business_name || client.name || "Blog";
    document.title = `${name} — Blog`;
  }, [client]);

  if (loading) return <div className="p-8">Loading…</div>;
  if (notFound) return <div className="p-8">Site not found.</div>;

  return (
    <main className="mx-auto max-w-4xl p-8">
      <header className="mb-10 border-b pb-6">
        <h1 className="text-4xl font-bold">{client?.business_name || client?.name}</h1>
        <p className="text-muted-foreground mt-2">Latest articles</p>
      </header>
      <ul className="space-y-8">
        {posts.length === 0 && <li className="text-muted-foreground">No articles yet.</li>}
        {posts.map((p) => (
          <li key={p.id} className="border-b pb-6">
            <Link to={`/sites/${clientSlug}/blog/${p.slug}`} className="block group">
              {p.featured_image && (
                <img
                  src={p.featured_image}
                  alt={p.title ?? ""}
                  loading="lazy"
                  className="w-full h-56 object-cover rounded mb-3"
                />
              )}
              <h2 className="text-2xl font-semibold group-hover:underline">{p.title}</h2>
              {p.meta_description && <p className="text-muted-foreground mt-2">{p.meta_description}</p>}
              {p.published_at && (
                <time className="text-xs text-muted-foreground mt-2 block">
                  {new Date(p.published_at).toLocaleDateString()}
                </time>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </main>
  );
}
